export const ISLAND_SSR_EMOTION_STYLE_ATTR = 'data-rp-island-ssr-emotion';

/** @type {boolean} */
let islandEmotionSheetFlushWrapped = false;

/** @type {Function | null} */
let patchedStyleSheetConstructor = null;

/** @type {Function | null} */
let originalStyleSheetPrototypeFlush = null;

/**
 * @param {unknown} sheet
 * @returns {sheet is { constructor: Function, flush: Function, isSpeedy: boolean, tags: unknown[] }}
 */
function isEmotionStyleSheet(sheet) {
	return sheet !== null
		&& typeof sheet === 'object'
		&& sheet.constructor !== Object
		&& typeof sheet.flush === 'function'
		&& typeof sheet.insert === 'function'
		&& typeof sheet.hydrate === 'function'
		&& typeof sheet.key === 'string'
		&& Array.isArray(sheet.tags)
		&& typeof sheet.isSpeedy === 'boolean'
		&& typeof sheet.ctr === 'number';
}

/**
 * @param {NodeListOf<HTMLStyleElement> | HTMLStyleElement[]} styleElements
 */
export function markIslandSsrEmotionStyleTags(styleElements) {
	for (const styleElement of styleElements) {
		styleElement.setAttribute(ISLAND_SSR_EMOTION_STYLE_ATTR, '');
	}
}

function createFlushPreservingIslandSsrEmotionTags() {
	return function flushPreservingIslandSsrEmotionTags() {
		const preservedTags = [];

		for (const tag of this.tags) {
			const isPreservedSsrTag = tag instanceof HTMLStyleElement
				&& tag.hasAttribute(ISLAND_SSR_EMOTION_STYLE_ATTR);

			if (isPreservedSsrTag) {
				preservedTags.push(tag);
				continue;
			}

			tag.parentNode?.removeChild(tag);
		}

		this.tags = preservedTags;

		if (this.isSpeedy) {
			this.ctr = 0;
			return;
		}

		this.ctr = preservedTags.length;
	};
}

/**
 * Emotion Global creates its own StyleSheet (`rp-island-global`) and calls flush() on unmount.
 * React Strict Mode runs that cleanup before the second insert pass, which was removing batch SSR
 * tags from the DOM during React Strict Mode cleanup.
 * @param {import('@emotion/cache').EmotionCache} cache
 */
export function wrapIslandEmotionSheetFlush(cache) {
	if (cache.sheet === undefined || typeof cache.sheet.flush !== 'function') {
		return;
	}

	const flushPreservingIslandSsrEmotionTags = createFlushPreservingIslandSsrEmotionTags();

	if (!islandEmotionSheetFlushWrapped && isEmotionStyleSheet(cache.sheet)) {
		patchedStyleSheetConstructor = cache.sheet.constructor;
		originalStyleSheetPrototypeFlush = patchedStyleSheetConstructor.prototype.flush;
		patchedStyleSheetConstructor.prototype.flush = flushPreservingIslandSsrEmotionTags;
		islandEmotionSheetFlushWrapped = true;
	}

	cache.sheet.flush = flushPreservingIslandSsrEmotionTags.bind(cache.sheet);
}

/**
 * Test-only reset when StyleSheet.prototype.flush was wrapped in a prior test file.
 */
export function resetIslandEmotionSheetFlushWrapForTests() {
	if (patchedStyleSheetConstructor !== null && originalStyleSheetPrototypeFlush !== null) {
		patchedStyleSheetConstructor.prototype.flush = originalStyleSheetPrototypeFlush;
	}

	patchedStyleSheetConstructor = null;
	originalStyleSheetPrototypeFlush = null;
	islandEmotionSheetFlushWrapped = false;
}
